import { isTravellerPreviewLink } from '@auth/features/auth/lib/preview-access';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { draftIdFromPreviewId } from '@travel-planner/shared/utils/draft-preview-id';
import { getAuthToken } from '@/shared/api/trpc';
import { rootRoute } from './_root';

// ─── Preview (public as the customer link; agent view needs a session) ──
// Parent layout route owns the <Outlet /> wrapper. Phase 2A will swap
// PreviewLayoutPage's bare wrapper for <PreviewShell><Outlet/></PreviewShell>
// so the shell mounts once and only the child <Outlet /> swaps between
// home / itinerary / map / stays / optionals.

// Canonical: traveller | consultant. Legacy alias `customer` → traveller
// (share/WhatsApp/embed links must keep working).
type PreviewSearch = {
  view?: 'traveller' | 'consultant' | 'customer';
  lang?: string;
  isEdit?: boolean;
  item?: string;
  token?: string;
  // XT-126: traveller-chosen display currency. Absent means the itinerary's
  // own base currency, so every existing link keeps its current behaviour;
  // `USD` is the only other accepted value. Display only — nothing is repriced.
  currency?: 'USD';
};

const normalizePreviewView = (raw: unknown): PreviewSearch['view'] => {
  if (raw === 'consultant') return 'consultant';
  if (raw === 'traveller' || raw === 'customer') return raw;
  return undefined;
};

const validatePreviewSearch = (
  input: Record<string, unknown>
): PreviewSearch => ({
  view: normalizePreviewView(input.view),
  lang: typeof input.lang === 'string' ? input.lang : undefined,
  isEdit: input.isEdit === true || input.isEdit === 'true' ? true : undefined,
  item: typeof input.item === 'string' ? input.item : undefined,
  token:
    typeof input.token === 'string' && input.token.length > 0
      ? input.token
      : undefined,
  currency: input.currency === 'USD' ? 'USD' : undefined,
});

type PvAliasSearch = {
  view?: 'traveller' | 'consultant' | 'customer';
  lang?: string;
  item?: string;
  token?: string;
};
const validatePvAliasSearch = (
  input: Record<string, unknown>
): PvAliasSearch => ({
  view: normalizePreviewView(input.view),
  lang: typeof input.lang === 'string' ? input.lang : undefined,
  item: typeof input.item === 'string' ? input.item : undefined,
  token:
    typeof input.token === 'string' && input.token.length > 0
      ? input.token
      : undefined,
});

export const previewLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'preview/$itineraryId',
  // Anything but the customer link renders the agent view, which legacy put
  // behind `auth.protect()` — see `isTravellerPreviewLink`. Gated here rather
  // than only in `AuthGuard` so consultant chrome never paints for a visitor
  // who is about to be bounced to sign-in.
  beforeLoad: async ({ location, params, search }) => {
    if (isTravellerPreviewLink(location.pathname, search)) return;
    // A Mini Planner draft has only the traveller view: there is no itinerary
    // behind it to edit, so it never paints the agent view.
    if (draftIdFromPreviewId(params.itineraryId)) {
      throw redirect({
        to: location.pathname,
        search: { ...search, view: 'traveller' },
        replace: true,
      });
    }
    if (await getAuthToken()) return;
    throw redirect({
      to: '/sign-in',
      search: { redirect_url: location.pathname + location.searchStr },
    });
  },
  // Token in query string — do not leak via Referer to utfs/Mapbox.
  head: () => ({
    meta: [{ name: 'referrer', content: 'no-referrer' }],
  }),
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-layout').then((m) => ({
      default: m.PreviewLayoutPage,
    }))
  ),
  validateSearch: validatePreviewSearch,
});

export const previewHomeRoute = createRoute({
  getParentRoute: () => previewLayoutRoute,
  path: '/',
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-home').then((m) => ({
      default: m.PreviewHomePage,
    }))
  ),
});

export const previewItineraryRoute = createRoute({
  getParentRoute: () => previewLayoutRoute,
  path: 'itinerary',
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-itinerary').then((m) => ({
      default: m.PreviewItineraryPage,
    }))
  ),
});

export const previewMapRoute = createRoute({
  getParentRoute: () => previewLayoutRoute,
  path: 'map',
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-map').then((m) => ({
      default: m.PreviewMapPage,
    }))
  ),
});

export const previewStaysRoute = createRoute({
  getParentRoute: () => previewLayoutRoute,
  path: 'stays',
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-stays').then((m) => ({
      default: m.PreviewStaysPage,
    }))
  ),
});

export const previewOptionalsRoute = createRoute({
  getParentRoute: () => previewLayoutRoute,
  path: 'optionals',
  component: lazyRouteComponent(() =>
    import('@preview/pages/preview-optionals').then((m) => ({
      default: m.PreviewOptionalsPage,
    }))
  ),
});

// ─── Legacy `/pv/{shortId}` alias ──────────────────────────────────────
// Resolves the 8-char short itinerary id (+ optional `?item=` prefix) and
// redirects to the canonical `/preview/$itineraryId/itinerary`. Keeps old
// WhatsApp / share links working. Rendering stays in the canonical route.
//
// Registered at BOTH depths on purpose: shared links are minted bare
// (`/pv/<short>`) as well as with the `/itinerary` suffix, and a bare one with
// no route fell through to the router's blank 404 instead of resolving.
const pvAliasComponent = lazyRouteComponent(() =>
  import('@preview/pages/pv-alias').then((m) => ({
    default: m.PvAliasPage,
  }))
);

export const pvAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'pv/$shortItineraryId/itinerary',
  component: pvAliasComponent,
  validateSearch: validatePvAliasSearch,
  head: () => ({
    meta: [{ name: 'referrer', content: 'no-referrer' }],
  }),
});

export const pvAliasShortRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'pv/$shortItineraryId',
  component: pvAliasComponent,
  validateSearch: validatePvAliasSearch,
  head: () => ({
    meta: [{ name: 'referrer', content: 'no-referrer' }],
  }),
});

// ─── Operation preview (public — externalMessage UUID is bearer credential)
// Literal "details" sibling registered alongside the $action param route.
// TanStack Router prioritises literal segments over params on the same
// depth, so `/operation-preview/details/<id>` resolves to the details
// route even though `$action` would otherwise match "details".

export const operationPreviewDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'operation-preview/details/$id',
  component: lazyRouteComponent(() =>
    import('@preview/pages/operation-preview-details').then((m) => ({
      default: m.OperationPreviewDetailsPage,
    }))
  ),
});

export const operationPreviewActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'operation-preview/$action/$id',
  component: lazyRouteComponent(() =>
    import('@preview/pages/operation-preview-action').then((m) => ({
      default: m.OperationPreviewActionPage,
    }))
  ),
});

// Public customer checkout (no auth). The payment-log id in the path is the
// bearer credential; the page fetches clientSecret + publishableKey via the
// public `itineraryPayments.getCheckout` procedure.
export const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'checkout/$id',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/checkout').then((m) => ({
      default: m.CheckoutPage,
    }))
  ),
});

export const checkoutSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'checkout/success',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/checkout').then((m) => ({
      default: () => m.CheckoutResultPage({ variant: 'success' }),
    }))
  ),
});

export const checkoutCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'checkout/cancel',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/checkout').then((m) => ({
      default: () => m.CheckoutResultPage({ variant: 'cancel' }),
    }))
  ),
});
